import React, { useState, useEffect } from "react"
import { useIntl, FormattedMessage } from "react-intl"
import Button from "react-bootstrap/Button"
import Form from "react-bootstrap/Form"
import InputGroup from "react-bootstrap/InputGroup"
import Container from "react-bootstrap/Container"
import Row from "react-bootstrap/Row"
import Col from "react-bootstrap/Col"

import { Search, PencilSquare, Trash, PlusCircle, Backspace, PersonBadge } from "react-bootstrap-icons" // Iconos de Bootstrap

import { AlertError, AlertLoading } from '../helpers/AlertHelper'

import FormModal from "../components/modals/FormModal"
import TableManagement from "../components/tables/TableManagement"

import UsersService from "../services/UsersService"

import UsersForm from "./UsersForm"

const Users = () => {
    const intl = useIntl()
    const [userId, setUserId] = useState(null)
    const [users, setUsers] = useState([])
    const [isLoaded, setIsLoaded] = useState(false)
    const [error, setError] = useState(null)
    const [currPage, setCurrPage] = useState(1)
    const [search, setSearch] = useState("")
    const [sortBy, setSortBy] = useState("name")
    const [sortOrder, setSortOrder] = useState("asc")
    const [totalRows, setTotalRows] = useState(0)
    const [perPage, setPerPage] = useState(20)

    // Setting modal title
    const [modalTitle, setModalTitle] = useState(false)

    // Setting modal icon
    const [modalIcon, setModalIcon] = useState(false)

    // Open modal form control to add/edit element
    const [modalTrigger, setModalTrigger] = useState(false)

    // Setting modal action enum[add,edit,delete]
    const [modalAction, setModalAction] = useState(false)

    // Fetch data
    const fetchUsers = async () => {
        try {
            const response = await UsersService.getUsers(currPage, search, perPage, sortBy, sortOrder)
            setUsers(response.data.users)
            setTotalRows(response.data.pagination.totalItems)
            setIsLoaded(true)
        } catch (err) {
            setIsLoaded(false)
            setError(err)
        }
    }

    const handleModal = () => {
        setModalTrigger(false)
        fetchUsers()
    }

    const handleSearchChange = (e) => {
        setSearch(e.target.value)
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    const handleSearchClean = () => {
        setSearch('')
        setCurrPage(1) // Reinicia a la primera página cuando se busca
    }

    // Re-fetch data on page/perPage change
    useEffect(() => {
        fetchUsers()
    }, [currPage, perPage, search, sortBy, sortOrder])

    const handleButtonClick = ({ data, type }) => {
        // Definir un objeto para mapear tipos a títulos e íconos
        const modalConfig = {
            add: {
                title: <FormattedMessage id="usersAdd" defaultMessage="Add user" />,
                icon: <PlusCircle />,
            },
            edit: {
                title: <FormattedMessage id="usersEdit" defaultMessage="Edit user" />,
                icon: <PencilSquare />,
            },
            delete: {
                title: <FormattedMessage id="usersDelete" defaultMessage="Delete user" />,
                icon: <Trash />,
            }
        }

        // Establecer la configuración del modal dependiendo del tipo
        const config = modalConfig[type] || {}

        if (config) {
            setUserId(type === 'add' ? false : data._id) // Si 'add', userid será falso
            setModalTitle(config.title) // Set title from the config
            setModalIcon(config.icon) // Set icon from the config
            setModalAction(type)
            setModalTrigger(true)
        }
    }

    const handleRowsPerPageChange = (newPerPage, page) => {
        setPerPage(newPerPage)
        setCurrPage(page)
    }

    const handleSort = async (column, sortDirection) => {
        setSortOrder(sortDirection)
        setSortBy(column.id)
        setCurrPage(1)
    }

    const columns = [
        {
            id: 'name',
            name: <FormattedMessage id="usersName" defaultMessage="Name" />,
            selector: (row) => row.name,
            sortable: true,
        },
        {
            id: 'role',
            name: <FormattedMessage id="usersRole" defaultMessage="Role" />,
            selector: (row) => row.role,
            sortable: true,
        },
        {
            id: 'email',
            name: <FormattedMessage id="usersEmail" defaultMessage="E-mail" />,
            selector: (row) => row.email,
            sortable: true,
        },
        {
            id: 'phone',
            name: <FormattedMessage id="usersPhoneNumber" defaultMessage="Phone" />,
            selector: (row) => row.phone,
        }
    ]


    return (
        <Container fluid className="p-0">
            <Row className="p-1 bg-secondary">
                <Col>
                    <InputGroup>
                        <InputGroup.Text><Search /></InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder={intl.formatMessage({ id: "textSearch", defaultMessage: "Search users..." })}
                            value={search}
                            onChange={handleSearchChange}
                        />
                        <Button onClick={handleSearchClean} variant="light">
                            <Backspace /> <FormattedMessage id="textClean" defaultMessage="Clean" />
                        </Button>

                        <Button onClick={() => handleButtonClick({ data: null, type: 'add' })} variant="primary">
                            <PlusCircle /> <FormattedMessage id="textAdd" defaultMessage="Add" />
                        </Button>
                    </InputGroup>
                </Col>
            </Row>
            {isLoaded ? (
                <TableManagement
                    icon={<PersonBadge />}
                    title={<FormattedMessage id="navUsers" defaultMessage="Users" />}
                    columns={columns}
                    data={users}
                    paginationPerPage={perPage}
                    paginationTotalRows={totalRows}
                    onChangePage={setCurrPage}
                    onChangeRowsPerPage={handleRowsPerPageChange}
                    onSort={handleSort}
                    onActionClick={handleButtonClick}
                />
            ) : error ? (
                <AlertError error={error} />
            ) : (
                <AlertLoading isLoaded={isLoaded} />
            )}

            <FormModal
                title={modalTitle}
                icon={modalIcon}
                body={UsersForm}
                refreshData={handleModal}
                modalTrigger={modalTrigger}
                elementId={userId}
                action={modalAction} />

        </Container>
    )
}

export default Users